import it from '../i18n/it.json';
import en from '../i18n/en.json';
import de from '../i18n/de.json';
import type { ContentValueType } from '../types/content';

type Locale = 'it' | 'en' | 'de';

type DefaultValueMap = Partial<Record<Locale, string | string[]>>;

export interface ContentDefinition {
  section: string;
  contentKey: string;
  label: string;
  helper?: string;
  type: ContentValueType;
  defaultValues: DefaultValueMap;
  placeholder?: string;
}

export const supportedLocales: Locale[] = ['it', 'en', 'de'];

const dictionaries: Record<Locale, unknown> = {
  it,
  en,
  de,
};

const getNestedValue = (tree: unknown, path: string): unknown => {
  if (!path) {
    return undefined;
  }

  return path.split('.').reduce<unknown>((acc, segment) => {
    if (acc === undefined || acc === null) {
      return undefined;
    }

    if (Array.isArray(acc)) {
      const index = Number(segment);
      if (Number.isInteger(index) && index >= 0 && index < acc.length) {
        return acc[index];
      }
      return undefined;
    }

    if (typeof acc === 'object') {
      return (acc as Record<string, unknown>)[segment];
    }

    return undefined;
  }, tree);
};

const buildDefaultsFromTranslations = (key: string): DefaultValueMap => {
  const defaults: DefaultValueMap = {};
  supportedLocales.forEach((locale) => {
    const value = getNestedValue(dictionaries[locale], key);
    if (value !== undefined) {
      defaults[locale] = value as string | string[];
    }
  });
  return defaults;
};

// Removed manualDefaults: defaults now pulled from static i18n dictionaries

export const contentDefinitions: ContentDefinition[] = [
  // ——— Social Highlights ———
  {
    section: 'Social · Instagram',
    contentKey: 'social.instagram.latestUrl',
    label: 'Instagram · URL ultimo post',
    helper: 'Incolla l’URL completo del post da mettere in evidenza.',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · Instagram',
    contentKey: 'social.instagram.embedCode',
    label: 'Instagram · Codice incorporamento',
    helper: 'Incolla qui il blocco HTML ottenuto da “Incorpora”.',
    type: 'longText',
    defaultValues: {},
  },
  {
    section: 'Social · Instagram',
    contentKey: 'social.instagram.title',
    label: 'Instagram · Titolo/Nota (opz.)',
    helper: 'Testo breve mostrato sulla card (opzionale).',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · Instagram',
    contentKey: 'social.instagram.cta',
    label: 'Instagram · Etichetta bottone',
    helper: 'Es. “Apri su Instagram”.',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · Instagram',
    contentKey: 'social.instagram.thumbnail',
    label: 'Instagram · Anteprima (URL opz.)',
    helper: 'URL immagine di anteprima (se disponibile).',
    type: 'text',
    defaultValues: {},
  },

  {
    section: 'Social · Facebook',
    contentKey: 'social.facebook.latestUrl',
    label: 'Facebook · URL ultimo post',
    helper: 'Incolla l’URL completo del post da mettere in evidenza.',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · Facebook',
    contentKey: 'social.facebook.embedCode',
    label: 'Facebook · Codice incorporamento (opz.)',
    helper: 'Incolla il codice embed della piattaforma (se desiderato).',
    type: 'longText',
    defaultValues: {},
  },
  {
    section: 'Social · Facebook',
    contentKey: 'social.facebook.title',
    label: 'Facebook · Titolo/Nota (opz.)',
    helper: 'Testo breve mostrato sulla card (opzionale).',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · Facebook',
    contentKey: 'social.facebook.cta',
    label: 'Facebook · Etichetta bottone',
    helper: 'Es. “Apri su Facebook”.',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · Facebook',
    contentKey: 'social.facebook.thumbnail',
    label: 'Facebook · Anteprima (URL opz.)',
    helper: 'URL immagine di anteprima (se disponibile).',
    type: 'text',
    defaultValues: {},
  },

  {
    section: 'Social · LinkedIn',
    contentKey: 'social.linkedin.latestUrl',
    label: 'LinkedIn · URL ultimo post',
    helper: 'Incolla l’URL completo del post da mettere in evidenza.',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · LinkedIn',
    contentKey: 'social.linkedin.embedCode',
    label: 'LinkedIn · Codice incorporamento (opz.)',
    helper: 'Incolla il codice embed della piattaforma (se desiderato).',
    type: 'longText',
    defaultValues: {},
  },
  {
    section: 'Social · LinkedIn',
    contentKey: 'social.linkedin.title',
    label: 'LinkedIn · Titolo/Nota (opz.)',
    helper: 'Testo breve mostrato sulla card (opzionale).',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · LinkedIn',
    contentKey: 'social.linkedin.cta',
    label: 'LinkedIn · Etichetta bottone',
    helper: 'Es. “Apri su LinkedIn”.',
    type: 'text',
    defaultValues: {},
  },
  {
    section: 'Social · LinkedIn',
    contentKey: 'social.linkedin.thumbnail',
    label: 'LinkedIn · Anteprima (URL opz.)',
    helper: 'URL immagine di anteprima (se disponibile).',
    type: 'text',
    defaultValues: {},
  },
  // ——— Navigazione & Footer ———
  {
    section: 'Navigazione',
    contentKey: 'nav.brandAlt',
    label: 'Logo · Testo alternativo',
    helper: 'Descrizione accessibile del logo usata anche per l’attributo alt.',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('nav.brandAlt'),
  },

  {
    section: 'Footer',
    contentKey: 'footer.brandAlt',
    label: 'Footer · Logo alt text',
    helper: 'Descrizione accessibile del logo nel footer.',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('footer.brandAlt'),
  },
  {
    section: 'Footer',
    contentKey: 'footer.links.labels',
    label: 'Footer · Etichette link',
    helper: 'Un elemento per riga (es. Privacy, Cookie, Contatti).',
    type: 'list',
    defaultValues: buildDefaultsFromTranslations('footer.links.labels'),
  },
  {
    section: 'Footer',
    contentKey: 'footer.links.ariaPrivacy',
    label: 'Footer · Aria label Privacy',
    helper: 'Testo accessibile annunciato dai lettori di schermo.',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('footer.links.ariaPrivacy'),
  },
  {
    section: 'Footer',
    contentKey: 'footer.links.ariaCookies',
    label: 'Footer · Aria label Cookie',
    helper: 'Testo accessibile per il pulsante di gestione cookie.',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('footer.links.ariaCookies'),
  },
  {
    section: 'Footer',
    contentKey: 'footer.links.ariaContacts',
    label: 'Footer · Aria label Contatti',
    helper: 'Testo accessibile per il link ai contatti.',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('footer.links.ariaContacts'),
  },

  {
    section: 'Hero',
    contentKey: 'hero.title',
    label: 'Titolo principale',
    helper: 'Testo grande evidenziato nella hero.',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('hero.title'),
  },
  {
    section: 'Hero',
    contentKey: 'hero.subtitle',
    label: 'Sottotitolo',
    helper: 'Paragrafo sotto al titolo principale.',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('hero.subtitle'),
  },
  {
    section: 'Hero',
    contentKey: 'hero.features',
    label: 'Punti di forza',
    helper: 'Un punto per riga. Appare con le icone a spunta.',
    type: 'list',
    defaultValues: buildDefaultsFromTranslations('hero.features'),
  },
  {
    section: 'Hero',
    contentKey: 'hero.badges',
    label: 'Badge superiori',
    helper: 'Tag mostrati sopra al titolo hero. Inserisci un elemento per riga.',
    type: 'list',
    defaultValues: buildDefaultsFromTranslations('hero.badges'),
  },
  {
    section: 'Hero',
    contentKey: 'hero.ctaPrimary',
    label: 'CTA primaria',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.ctaPrimary'),
  },
  {
    section: 'Hero',
    contentKey: 'hero.ctaSecondary',
    label: 'CTA secondaria',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.ctaSecondary'),
  },
  {
    section: 'Hero',
    contentKey: 'hero.pills',
    label: 'Pills evidenziate',
    helper: 'Brevi badge che compaiono sotto le CTA. Inserisci un elemento per riga.',
    type: 'list',
    defaultValues: buildDefaultsFromTranslations('hero.pills'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.videoOverline',
    label: 'Video · Overline',
    helper: 'Testo piccolo sopra il box video.',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.videoOverline'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.videoTitle',
    label: 'Video · Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('hero.media.videoTitle'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.videoBody',
    label: 'Video · Descrizione',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('hero.media.videoBody'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.videoCta',
    label: 'Video · CTA',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.videoCta'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageOverline',
    label: 'Showcase · Overline',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageOverline'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageTitle',
    label: 'Showcase · Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageTitle'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageBody',
    label: 'Showcase · Descrizione',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageBody'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageBadgePrimary',
    label: 'Showcase · Highlight',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageBadgePrimary'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageBadgeStatus',
    label: 'Showcase · Stato',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageBadgeStatus'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageCardOneLabel',
    label: 'Showcase · Label card 1',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageCardOneLabel'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageCardTwoLabel',
    label: 'Showcase · Label card 2',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageCardTwoLabel'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageFootnoteStatus',
    label: 'Showcase · Stato descrizione',
    helper: 'Testo a destra di "Ready".',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageFootnoteStatus'),
  },
  {
    section: 'Hero · Media',
    contentKey: 'hero.media.imageFootnoteDetail',
    label: 'Showcase · Dettaglio',
    helper: 'Testo sotto le miniature a sinistra.',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('hero.media.imageFootnoteDetail'),
  },
  {
    section: 'Percorsi',
    contentKey: 'percorsi.eyebrow',
    label: 'Sezione · Eyebrow',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.eyebrow'),
  },
  {
    section: 'Percorsi',
    contentKey: 'percorsi.title',
    label: 'Sezione · Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('percorsi.title'),
  },
  {
    section: 'Percorsi',
    contentKey: 'percorsi.subtitle',
    label: 'Sezione · Sottotitolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('percorsi.subtitle'),
  },
  {
    section: 'Percorsi',
    contentKey: 'percorsi.cta',
    label: 'Sezione · CTA',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.cta'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.overline',
    label: 'Visual · Overline',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.overline'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.title',
    label: 'Visual · Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.title'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.body',
    label: 'Visual · Descrizione',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.body'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.badges',
    label: 'Visual · Badge',
    helper: 'Elementi visualizzati come etichette tonde. Inserisci un elemento per riga.',
    type: 'list',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.badges'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.video.overline',
    label: 'Slot verticale · Overline',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.video.overline'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.video.body',
    label: 'Slot verticale · Descrizione',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.video.body'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.video.badge',
    label: 'Slot verticale · Badge',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.video.badge'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.carousel.overline',
    label: 'Carousel · Overline',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.carousel.overline'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.carousel.body',
    label: 'Carousel · Descrizione',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.carousel.body'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.carousel.helper',
    label: 'Carousel · Nota',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.carousel.helper'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.gallery.overline',
    label: 'Gallery studenti · Overline',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.gallery.overline'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.gallery.body',
    label: 'Gallery studenti · Descrizione',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.gallery.body'),
  },
  {
    section: 'Percorsi · Visual',
    contentKey: 'percorsi.visual.gallery.helper',
    label: 'Gallery studenti · Nota',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('percorsi.visual.gallery.helper'),
  },
  {
    section: 'Value Props',
    contentKey: 'vantaggi.eyebrow',
    label: 'Eyebrow',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('vantaggi.eyebrow'),
  },
  {
    section: 'Value Props',
    contentKey: 'vantaggi.title',
    label: 'Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('vantaggi.title'),
  },
  {
    section: 'Value Props',
    contentKey: 'vantaggi.subtitle',
    label: 'Sottotitolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('vantaggi.subtitle'),
  },
  {
    section: 'Value Props · Media',
    contentKey: 'value.media.overline',
    label: 'Media · Overline',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('value.media.overline'),
  },
  {
    section: 'Value Props · Media',
    contentKey: 'value.media.headline',
    label: 'Media · Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('value.media.headline'),
  },
  {
    section: 'Value Props · Media',
    contentKey: 'value.media.body',
    label: 'Media · Descrizione',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('value.media.body'),
  },
  {
    section: 'Value Props · Media',
    contentKey: 'value.media.badges',
    label: 'Media · Badge',
    helper: 'Inserisci un elemento per riga (es. KPI, benefit).',
    type: 'list',
    defaultValues: buildDefaultsFromTranslations('value.media.badges'),
  },
  {
    section: 'Value Props · Media',
    contentKey: 'value.media.videoTitle',
    label: 'Video manifesto · Titolo',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('value.media.videoTitle'),
  },
  {
    section: 'Value Props · Media',
    contentKey: 'value.media.videoBadge',
    label: 'Video manifesto · Badge',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('value.media.videoBadge'),
  },
  {
    section: 'Value Props · Media',
    contentKey: 'value.media.thumbLabels',
    label: 'Miniature · Etichette',
    helper: 'Etichette delle tre miniature. Inserisci tre righe.',
    type: 'list',
    defaultValues: buildDefaultsFromTranslations('value.media.thumbLabels'),
  },
  {
    section: 'Trust Stats',
    contentKey: 'stats.modules',
    label: 'Modulo · Valore',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('stats.modules'),
  },
  {
    section: 'Trust Stats',
    contentKey: 'stats.modulesLabel',
    label: 'Modulo · Descrizione',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('stats.modulesLabel'),
  },
  {
    section: 'Trust Stats',
    contentKey: 'stats.students',
    label: 'Studenti · Valore',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('stats.students'),
  },
  {
    section: 'Trust Stats',
    contentKey: 'stats.studentsLabel',
    label: 'Studenti · Descrizione',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('stats.studentsLabel'),
  },
  {
    section: 'Trust Stats',
    contentKey: 'stats.rating',
    label: 'Rating · Valore',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('stats.rating'),
  },
  {
    section: 'Trust Stats',
    contentKey: 'stats.ratingLabel',
    label: 'Rating · Descrizione',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('stats.ratingLabel'),
  },
  {
    section: 'Trust Stats',
    contentKey: 'stats.disclaimer',
    label: 'Nota fondo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('stats.disclaimer'),
  },
  {
    section: 'KPI',
    contentKey: 'kpi.eyebrow',
    label: 'Eyebrow',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('kpi.eyebrow'),
  },
  {
    section: 'KPI',
    contentKey: 'kpi.title',
    label: 'Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('kpi.title'),
  },
  {
    section: 'KPI',
    contentKey: 'kpi.subtitle',
    label: 'Sottotitolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('kpi.subtitle'),
  },
  {
    section: 'Final CTA',
    contentKey: 'cta.title',
    label: 'Titolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('cta.title'),
  },
  {
    section: 'Final CTA',
    contentKey: 'cta.subtitle',
    label: 'Sottotitolo',
    type: 'longText',
    defaultValues: buildDefaultsFromTranslations('cta.subtitle'),
  },
  {
    section: 'Final CTA',
    contentKey: 'cta.button',
    label: 'Testo bottone',
    type: 'text',
    defaultValues: buildDefaultsFromTranslations('cta.button'),
  },
];

export const getDefaultValueForLocale = (
  definition: ContentDefinition,
  locale: Locale,
): string | string[] => {
  const base = definition.defaultValues[locale];
  if (base !== undefined) {
    return Array.isArray(base) ? [...base] : base;
  }

  const fallback = definition.defaultValues.it;
  return Array.isArray(fallback) ? [...fallback] : (fallback ?? '');
};
